import { Component, ErrorInfo, ReactNode } from 'react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle, faSync } from '@fortawesome/free-solid-svg-icons'
import { Button } from '@/components/ui/button'

interface Props {
	children: ReactNode
	fallback?: ReactNode
	onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface State {
	hasError: boolean
	error?: Error
}

export class ErrorBoundary extends Component<Props, State> {
	public state: State = {
		hasError: false
	}

	public static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error }
	}

	public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error('ErrorBoundary caught an error:', error, errorInfo)
		
		// Call optional error handler
		if (this.props.onError) {
			this.props.onError(error, errorInfo)
		}
	}

	private handleRetry = () => {
		this.setState({ hasError: false, error: undefined })
	}

	public render() {
		if (this.state.hasError) {
			// Custom fallback UI
			if (this.props.fallback) {
				return this.props.fallback
			}

			// Default error UI
			return (
				<div className="p-6 space-y-4">
					<Alert variant="destructive">
						<FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4" />
						<AlertDescription>
							<div className="space-y-2">
								<p className="font-medium">Something went wrong</p>
								<p className="text-sm text-gray-600">
									{this.state.error?.message || 'An unexpected error occurred'}
								</p>
								<Button 
									variant="outline" 
									size="sm" 
									onClick={this.handleRetry}
									className="mt-2"
								>
									<FontAwesomeIcon icon={faSync} className="w-4 h-4 mr-2" />
									Try Again
								</Button>
							</div>
						</AlertDescription>
					</Alert>
				</div>
			)
		}

		return this.props.children
	}
}

// Chart-specific error boundary
export class ChartErrorBoundary extends Component<Props, State> {
	public state: State = {
		hasError: false
	}

	public static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error }
	}

	public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error('ChartErrorBoundary caught an error:', error, errorInfo)
		
		if (this.props.onError) {
			this.props.onError(error, errorInfo)
		}
	}

	private handleRetry = () => {
		this.setState({ hasError: false, error: undefined })
	}

	public render() {
		if (this.state.hasError) {
			return (
				<div className="w-full h-[300px] flex items-center justify-center">
					<div className="text-center space-y-4">
						<FontAwesomeIcon icon={faExclamationTriangle} className="h-12 w-12 text-red-500 mx-auto" />
						<div>
							<p className="font-medium text-gray-900">Chart failed to load</p>
							<p className="text-sm text-gray-600 mt-1">
								{this.state.error?.message || 'Unable to render chart'}
							</p>
						</div>
						<Button 
							variant="outline" 
							size="sm" 
							onClick={this.handleRetry}
						>
							<FontAwesomeIcon icon={faSync} className="w-4 h-4 mr-2" />
							Retry
						</Button>
					</div>
				</div>
			)
		}

		return this.props.children
	}
}